import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams } from "react-router-dom";
import { Permission } from "../../../Model/Setting/Permission";
import { ROLE_PERMISSION_TITLE } from "../../../Routes/Setting/RolePermission";
import { setToast } from "../../../Util/Toast";
import { useApi } from "../../../Controller/ApiController";
import { useEffect, useState } from "react";
import CustomSkeleton from "../../../Components/CustomSkeleton";
import CustomButton from "../../../Components/Button/CustomButton";
import { Breadcrumb } from "react-bootstrap";

const AddUpdate: React.FC = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(300);
  const [totalData, setTotalData] = useState(0);
  const { apiGet, apiPatch } = useApi();
  const [permissionslist, setPermissionslist] = useState<any[]>([]);
  const navigate = useNavigate();
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [selectAllPermissions, setSelectAllPermissions] = useState<
    Record<string, boolean>
  >({});

  const handleSelectAllToggle = (heading: string) => {
    setSelectAllPermissions((prev) => ({
      ...prev,
      [heading]: !prev[heading],
    }));

    const allPermissionIds = groupedPermissions[heading].map(
      (item: any) => item.id,
    );
    setSelectedPermissions((prevSelected) =>
      selectAllPermissions[heading]
        ? prevSelected.filter((id) => !allPermissionIds.includes(id))
        : [...prevSelected, ...allPermissionIds],
    );
  };

  const checkall = () => {
    const allPermissionIds: number[] = [];

    Object.values(groupedPermissions).forEach((permissions: any) => {
      permissions.forEach((item: any) => {
        allPermissionIds.push(item.id);
      });
    });

    setSelectedPermissions((prevSelected) =>
      selectAllPermissions["all"]
        ? []
        : [...Array.from(new Set([...prevSelected, ...allPermissionIds]))],
    );

    setSelectAllPermissions((prev) => {
      const newSelectAll: any = { ...prev, all: !prev["all"] };
      Object.keys(groupedPermissions).forEach((heading) => {
        newSelectAll[heading] = !prev["all"];
      });
      return newSelectAll;
    });
  };

  // Fetch permissions page-wise and append
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const permissionData = await Permission.$query().get(limit, page);
        const newPermissions = permissionData[0]?.$response?.data.data;
        const total = permissionData[0]?.$response?.data?.meta?.total;

        setPermissionslist((prev) => {
          const existingIds = new Set(prev.map((p: any) => p.id));
          const merged = [
            ...prev,
            ...newPermissions.filter((p: any) => !existingIds.has(p.id)),
          ];
          return merged;
        });

        setTotalData(total);
        setLoading(false);
      } catch (error: any) {
        setToast("error", error.response?.data?.message || "An error occurred");
      }
    };

    fetchPermissions();
  }, [page]);

  // Fetch role permissions
  useEffect(() => {
    if (!totalData) return;

    const fetchRolePermissions = async () => {
      try {
        const rolePermissionData: any = await apiGet(
          `roles/${id}/permissions?limit=${totalData}`,
        );
        const rolePermissionIds = rolePermissionData.data.map(
          (item: any) => item.id,
        );
        setSelectedPermissions(rolePermissionIds);
      } catch (error: any) {
        setToast("error", error.response?.data?.message || "An error occurred");
      }
    };

    fetchRolePermissions();
  }, [id, totalData]);

  const groupedPermissions: Record<string, Permission[]> =
    permissionslist.reduce(
      (acc, permission) => {
        const [checkboxLabel, ...rest] = permission.name.split("_");
        const heading = rest.join("_").replace(/_/g, " ");
        if (!acc[heading]) {
          acc[heading] = [];
        }
        acc[heading].push({ ...permission, checkboxLabel });
        return acc;
      },
      {} as Record<string, Permission[]>,
    );

  useEffect(() => {
    const allSelected = Object.values(groupedPermissions).every(
      (permissions: any) =>
        permissions.every((item: any) => selectedPermissions.includes(item.id)),
    );

    setSelectAllPermissions((prev) => {
      const newSelectAll: Record<string, boolean> = {
        ...prev,
        all: allSelected,
      };
      Object.keys(groupedPermissions).forEach((heading) => {
        const allGroupSelected = groupedPermissions[heading].every(
          (item: any) => selectedPermissions.includes(item.id),
        );
        newSelectAll[heading] = allGroupSelected;
      });

      const isSame =
        Object.keys(newSelectAll).length === Object.keys(prev).length &&
        Object.keys(newSelectAll).every(
          (key) => newSelectAll[key] === prev[key],
        );

      return isSame ? prev : newSelectAll;
    });
  }, [selectedPermissions, groupedPermissions]);

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCustomSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const inputData = { resources: selectedPermissions };

    try {
      await apiPatch(`roles/${id}/permissions/sync`, inputData);
      setToast("success", `${ROLE_PERMISSION_TITLE} Successfully Updated`);
      navigate(-1);
    } catch (error: any) {
      setToast("error", error.response?.data?.message || "Update failed");
    }
  };

  if (loading && permissionslist.length === 0) {
    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <CustomSkeleton
            key={index}
            count={1}
            height={95}
            customOuterStyle={{ marginTop: "10px", width: "100%" }}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/roles">Roles</Breadcrumb.Item>
        <Breadcrumb.Item active className="fw-bold">
          Edit Role Permissions
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="row">
        <form onSubmit={handleCustomSubmit}>
          <div className="d-flex align-items-center justify-content-between sticky-top-panel">
            <div>
              <input
                type="checkbox"
                id="selectAllCheckbox"
                className="form-check-input border-dark text-capitalize mx-1"
                onChange={checkall}
                checked={selectAllPermissions.all}
              />
              <label htmlFor="selectAllCheckbox" className="fw-bold">
                Select All Permissions
              </label>
            </div>
            <CustomButton
              customeClass="mb-1"
              label="Back"
              variant="secondary"
              onClick={handleGoBack}
            />
          </div>

          <table className="table table-bordered text-center align-middle">
            <thead className="table-light sticky-thead">
              <tr>
                <th>Permission</th>
                <th>Add</th>
                <th>Update</th>
                <th>Delete</th>
                <th>Restore</th>
                <th>Approved</th>
                <th>Rejected</th>
                <th>Select All</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedPermissions).map(
                ([heading, permissions]: any) => {
                  const permissionMap: Record<string, any> = {};
                  permissions.forEach((perm: any) => {
                    permissionMap[perm.checkboxLabel.toLowerCase()] = perm;
                  });

                  return (
                    <tr key={heading}>
                      <td className="text-start text-capitalize">
                        {heading.toLowerCase()}
                      </td>

                      {[
                        "add",
                        "update",
                        "delete",
                        "restore",
                        "approved",
                        "rejected",
                      ].map((action) => {
                        const item = permissionMap[action];

                        if (
                          (action === "approved" || action === "rejected") &&
                          !item
                        ) {
                          return <td key={action}></td>;
                        }

                        return (
                          <td key={action}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={
                                item
                                  ? selectedPermissions.includes(item.id)
                                  : false
                              }
                              onChange={() => {
                                if (item) {
                                  handlePermissionToggle(item.id);
                                }
                              }}
                            />
                          </td>
                        );
                      })}

                      <td>
                        <div>
                          <input
                            type="checkbox"
                            id={`selectall-${heading}`}
                            style={{ marginTop: "0.8rem", marginRight: "5px" }}
                            className="form-check-input border-dark text-capitalize"
                            checked={selectAllPermissions[heading]}
                            onChange={() => handleSelectAllToggle(heading)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>

          {permissionslist.length < totalData && (
            <p
              style={{ cursor: "pointer", textAlign: "end" }}
              className="text-primary"
              onClick={() => setPage((prev) => prev + 1)}
            >
              Show More...
            </p>
          )}

          <div className="my-3 d-flex justify-content-end">
            <button type="submit" className="btn btn-primary">
              Submit
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddUpdate;
